import { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import { STORAGE_KEYS } from '@/constants'
import { useSettings } from './SettingsContext'

/** @typedef {import('@/types').CartItem} CartItem */
/** @typedef {{ items: CartItem[], isOpen: boolean }} CartState */

const initialState = /** @type {CartState} */ ({ items: [], isOpen: false })

/** Build a stable cart line key from a product + variant. */
const variantKey = (productId, size, color) =>
  `${productId}::${size ?? ''}::${color ?? ''}`

/**
 * @param {CartState} state
 * @param {{type: string, payload?: any}} action
 * @returns {CartState}
 */
function cartReducer(state, action) {
  switch (action.type) {
    case 'HYDRATE':
      return { ...state, items: action.payload ?? [] }

    case 'ADD_ITEM': {
      const { product, quantity = 1, selectedSize, selectedColor } = action.payload
      const id = variantKey(product.id, selectedSize, selectedColor)
      const existing = state.items.find((it) => it.id === id)
      if (existing) {
        const newQty = Math.min(existing.quantity + quantity, product.stock)
        return {
          ...state,
          items: state.items.map((it) => (it.id === id ? { ...it, quantity: newQty } : it)),
        }
      }
      const newItem = /** @type {CartItem} */ ({
        id,
        productId: product.id,
        name: product.name,
        price: product.price,
        // The chosen colour's own photo, when it has one, so the cart shows
        // the pink one when they picked pink.
        image:
          product.colors?.find((c) => c.name === selectedColor)?.images?.[0] ?? product.images[0],
        selectedSize,
        selectedColor,
        quantity: Math.min(quantity, product.stock),
        stock: product.stock,
      })
      return { ...state, items: [...state.items, newItem] }
    }

    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((it) => it.id !== action.payload.id) }

    case 'UPDATE_QUANTITY': {
      const { id, quantity } = action.payload
      return {
        ...state,
        items: state.items
          .map((it) =>
            it.id === id ? { ...it, quantity: Math.max(1, Math.min(quantity, it.stock)) } : it
          )
          .filter((it) => it.quantity > 0),
      }
    }

    case 'CLEAR_CART':
      return { ...state, items: [] }

    case 'OPEN_DRAWER':
      return { ...state, isOpen: true }
    case 'CLOSE_DRAWER':
      return { ...state, isOpen: false }
    case 'TOGGLE_DRAWER':
      return { ...state, isOpen: !state.isOpen }

    default:
      return state
  }
}

const CartContext = createContext(null)

export function CartProvider({ children }) {
  const { deliveryFee: storeDeliveryFee, freeDeliveryThreshold } = useSettings()
  const [state, dispatch] = useReducer(cartReducer, initialState)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.cart)
      if (raw) dispatch({ type: 'HYDRATE', payload: JSON.parse(raw) })
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    const id = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEYS.cart, JSON.stringify(state.items))
      } catch {
        /* ignore */
      }
    }, 300)
    return () => clearTimeout(id)
  }, [state.items])

  const value = useMemo(() => {
    const cartCount = state.items.reduce((sum, it) => sum + it.quantity, 0)
    const cartTotal = state.items.reduce((sum, it) => sum + it.price * it.quantity, 0)
    // Display only — the backend reprices delivery when the order is created.
    // Before checkout the zone is unknown, so this is the inside-Dhaka fee;
    // the checkout page recomputes from the customer's actual choice.
    const deliveryFee =
      cartTotal === 0 ? 0 : cartTotal >= freeDeliveryThreshold ? 0 : storeDeliveryFee
    return {
      items: state.items,
      isOpen: state.isOpen,
      cartCount,
      cartTotal,
      deliveryFee,
      grandTotal: cartTotal + deliveryFee,
      addItem: (product, opts = {}) =>
        dispatch({ type: 'ADD_ITEM', payload: { product, ...opts } }),
      removeItem: (id) => dispatch({ type: 'REMOVE_ITEM', payload: { id } }),
      updateQuantity: (id, quantity) =>
        dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } }),
      clearCart: () => dispatch({ type: 'CLEAR_CART' }),
      openDrawer: () => dispatch({ type: 'OPEN_DRAWER' }),
      closeDrawer: () => dispatch({ type: 'CLOSE_DRAWER' }),
      toggleDrawer: () => dispatch({ type: 'TOGGLE_DRAWER' }),
    }
    // The store settings belong here too: when they arrive from the API the
    // cart's delivery line has to recompute, or it keeps showing the env
    // fallback for the rest of the session.
  }, [state, storeDeliveryFee, freeDeliveryThreshold])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within <CartProvider>')
  return ctx
}
